/**
 * Which tasks will go to Claude on their own — queued (server/claudeQueue.ts)
 * with a start mode other than Manual, its own or a queued ancestor's — for the board row's orange ring. Every
 * undone task with a repo is queued unless hidden, so a ring on each would say
 * nothing; the ones that will start without anyone pressing Send are the news. The queue is admin-only, so only an admin's tab
 * reads it: on every socket (re)connect (`loadQueuedTasks`), and whenever the
 * server says it moved (`claudeQueued`, coalesced to one per tick, carrying the
 * ids — applied as they are, no GET).
 */
import { ref } from "vue";
import { api } from "./api.js";
import { startModeOf, type QueuedClaude } from "@shared/tmux.js";

export const queuedTaskIds = ref<ReadonlySet<string>>(new Set());

/** Bumped on every `claudeQueued`: what a view listing the queue itself
 *  (TmuxView) watches to reread it. */
export const queueGen = ref(0);

let reading = 0;

export async function loadQueuedTasks(): Promise<void> {
  const mine = ++reading;
  try {
    const { queue } = await api.get<{ queue: QueuedClaude[] }>("/api/tmux/queue");
    // A hint that landed since is newer: only the latest may stand.
    if (mine !== reading) return;
    queuedTaskIds.value = new Set(queue.filter((q) => startModeOf(q) !== "manual").map((q) => q.taskId));
    // A (re)connect: what moved while the socket was down, the tmux view rereads too.
    queueGen.value++;
  } catch {
    // Keep what is held; the next hint or reconnect reads it again.
  }
}

/** The server's `claudeQueued`: the ids, as they are now. */
export function queuedMoved(ids: readonly string[]): void {
  reading++;
  queuedTaskIds.value = new Set(ids);
  queueGen.value++;
}

export function forgetQueuedTasks(): void {
  reading++;
  queuedTaskIds.value = new Set();
}
